import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

/* ─── helpers ─────────────────────────────────────────── */

function getStaffImageSrc(imagePath) {
  if (!imagePath) return null;
  let cleaned = String(imagePath).trim().replace(/\\/g, '/').replace(/^\/+/, '');
  cleaned = cleaned.replace(/^staff_images\//, '');
  return getImageURL(`/staff_images/${encodeURIComponent(cleaned)}`);
}

function initials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}

function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StatusBadge({ status }) {
  const normalized = String(status || '').trim().toLowerCase();
  let cls = 'border border-gray-200 bg-gray-100 text-gray-500';
  let label = 'Not Set';
  if (normalized === 'ongoing') {
    cls = 'border border-emerald-200 bg-emerald-50 text-emerald-700';
    label = 'Ongoing';
  } else if (normalized === 'completed') {
    cls = 'border border-slate-200 bg-slate-100 text-slate-600';
    label = 'Completed';
  } else if (normalized === 'not started') {
    cls = 'border border-amber-200 bg-amber-50 text-amber-700';
    label = 'Not Started';
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function ScheduleBadge({ scheduleType }) {
  const t = String(scheduleType || '').toLowerCase();
  if (t === 'weekend') {
    return (
      <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
        Weekend
      </span>
    );
  }
  if (t === 'weekday') {
    return (
      <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
        Weekday
      </span>
    );
  }
  return null;
}

function SummaryDot({ color, label, count }) {
  return (
    <span className="flex items-center gap-1 text-xs text-gray-600">
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      {count} {label}
    </span>
  );
}

/* ─── subject rows table inside each lecturer card ────── */

const STATUS_ORDER = { ongoing: 0, 'not started': 1, completed: 2 };

/* ─── batch row inside a subject group ─────────────────── */

function BatchRow({ s, no }) {
  const isGroup = s.type === 'group';
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* NO */}
      <td className="px-4 py-3 text-center text-xs text-gray-400 font-medium">{no}</td>
      {/* Batch / Cohort */}
      <td className="px-4 py-3">
        {isGroup ? (
          <div>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5.477-3.694M9 20H4v-2a4 4 0 015.477-3.694M15 7a4 4 0 11-8 0 4 4 0 018 0zm6 3a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Combined · {s.groupSubjects.length} batch{s.groupSubjects.length !== 1 ? 'es' : ''}
              {s.batchGroupName && (
                <span className="ml-1 border-l border-indigo-200 pl-1 text-indigo-500">{s.batchGroupName}</span>
              )}
            </span>
            <div className="mt-1.5 flex flex-col gap-0.5">
              {s.groupSubjects.map((gs) => (
                <span key={gs.batchSubjectId} className="text-xs text-gray-500">
                  {gs.cohortLabel}
                  {gs.courseShortName && (
                    <span className="ml-1 text-gray-400">({gs.courseShortName})</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div className="text-gray-700 text-sm">{s.cohortLabel}</div>
            {s.courseShortName && (
              <div className="mt-0.5 text-xs text-gray-400">{s.courseShortName}</div>
            )}
          </div>
        )}
      </td>
      {/* Schedule */}
      <td className="px-4 py-3">
        <ScheduleBadge scheduleType={s.scheduleType} />
      </td>
      {/* Enrolled */}
      <td className="px-4 py-3 text-center">
        <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-full bg-indigo-50 px-2 text-xs font-semibold text-indigo-700">
          {s.enrolledCount}
        </span>
      </td>
      {/* Start */}
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(s.startDate)}</td>
      {/* End */}
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(s.endDate)}</td>
      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge status={s.status} />
      </td>
    </tr>
  );
}

function SubjectTable({ subjects, filter }) {
  const filtered = useMemo(() => {
    if (filter === 'all') return subjects;
    return subjects.filter((s) => (s.status || 'not set') === filter);
  }, [subjects, filter]);

  // Group by subject name + code
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((s) => {
      const key = `${s.subjectName || ''}||${s.subjectCode || ''}`;
      if (!map.has(key)) map.set(key, { subjectName: s.subjectName, subjectCode: s.subjectCode, items: [] });
      map.get(key).items.push(s);
    });
    // Sort groups: ongoing first, then not started, then completed
    return [...map.values()].sort((a, b) => {
      const aMin = Math.min(...a.items.map((i) => STATUS_ORDER[i.status] ?? 3));
      const bMin = Math.min(...b.items.map((i) => STATUS_ORDER[i.status] ?? 3));
      return aMin - bMin || (a.subjectName || '').localeCompare(b.subjectName || '');
    });
  }, [filtered]);

  // Track which groups are expanded — all hidden by default
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!groups.length) {
    return (
      <p className="py-6 text-center text-sm text-gray-400">
        No subjects match the selected filter.
      </p>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {groups.map((grp, gi) => {
        const key = `${grp.subjectName}||${gi}`;
        const isOpen = !!expanded[key];
        return (
          <div key={key}>
            {/* Subject header — clickable to toggle */}
            <button
              type="button"
              onClick={() => toggle(key)}
              className="w-full flex items-center gap-3 bg-gray-50 px-5 py-3 text-left hover:bg-gray-100 transition-colors"
            >
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                {gi + 1}
              </span>
              <div>
                <span className="font-semibold text-gray-800 text-sm">{grp.subjectName || '—'}</span>
                {grp.subjectCode && (
                  <span className="ml-2 text-xs text-gray-400">{grp.subjectCode}</span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2 text-xs text-gray-400">
                <span>{grp.items.length} batch assignment{grp.items.length !== 1 ? 's' : ''}</span>
                {(() => {
                  const wd = grp.items.filter((i) => i.scheduleType === 'weekday').length;
                  const we = grp.items.filter((i) => i.scheduleType === 'weekend').length;
                  return (
                    <>
                      {wd > 0 && (
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-600 border border-blue-100">
                          {wd} weekday
                        </span>
                      )}
                      {we > 0 && (
                        <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-600 border border-purple-100">
                          {we} weekend
                        </span>
                      )}
                    </>
                  );
                })()}
                {/* chevron */}
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            {/* Batch rows — hidden by default */}
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                      <th className="whitespace-nowrap px-4 py-2 text-center font-semibold">No</th>
                      <th className="whitespace-nowrap px-4 py-2 text-left font-semibold">Batch / Cohort</th>
                      <th className="whitespace-nowrap px-4 py-2 text-left font-semibold">Schedule</th>
                      <th className="whitespace-nowrap px-4 py-2 text-center font-semibold">Enrolled</th>
                      <th className="whitespace-nowrap px-4 py-2 text-left font-semibold">Start</th>
                      <th className="whitespace-nowrap px-4 py-2 text-left font-semibold">End</th>
                      <th className="whitespace-nowrap px-4 py-2 text-left font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {grp.items
                      .slice()
                      .sort((a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3))
                      .map((s, bi) => (
                        <BatchRow
                          key={s.type === 'group' ? `group-${s.batchGroupId}` : `solo-${s.batchSubjectId}`}
                          s={s}
                          no={bi + 1}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── staff avatar ────────────────────────────────────── */

function StaffAvatar({ imagePath, name }) {
  const [imgError, setImgError] = useState(false);
  const imgSrc = getStaffImageSrc(imagePath);
  const showImage = imgSrc && !imgError;
  return (
    <div className="h-12 w-12 flex-shrink-0 rounded-full border border-gray-200 overflow-hidden bg-indigo-100 flex items-center justify-center">
      {showImage ? (
        <img
          src={imgSrc}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-sm font-semibold text-indigo-700">{initials(name)}</span>
      )}
    </div>
  );
}

/* ─── single lecturer card ─────────────────────────────── */

function LecturerCard({ lecturer }) {
  const [filter, setFilter] = useState('ongoing');

  const filterCounts = useMemo(() => ({
    all: lecturer.subjects.length,
    ongoing: lecturer.ongoingCount,
    'not started': lecturer.notStartedCount,
    completed: lecturer.completedCount,
  }), [lecturer]);

  const FILTER_OPTIONS = [
    { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    { value: 'ongoing', label: 'Ongoing', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { value: 'not started', label: 'Not Started', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { value: 'completed', label: 'Completed', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Card header — lecturer info */}
      <div className="flex items-center justify-between gap-4 px-5 py-4">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <StaffAvatar imagePath={lecturer.imagePath} name={lecturer.name} />

          {/* Name + details */}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-900">
                {lecturer.title ? `${lecturer.title} ` : ''}{lecturer.name}
              </span>
              {lecturer.type && (
                <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                  {lecturer.type}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs text-gray-500">
              {lecturer.position && <span className="mr-2">{lecturer.position}</span>}
              {lecturer.officeEmail && (
                <span className="text-gray-400">{lecturer.officeEmail}</span>
              )}
            </div>
          </div>
        </div>

        {/* Right side: subject count + summary dots */}
        <div className="hidden flex-shrink-0 items-center gap-3 sm:flex">
          <div className="flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {new Set(lecturer.subjects.map((s) => `${s.subjectName}||${s.subjectCode}`)).size} Subject{new Set(lecturer.subjects.map((s) => `${s.subjectName}||${s.subjectCode}`)).size !== 1 ? 's' : ''}
          </div>
          <span className="text-gray-200">|</span>
          <SummaryDot color="bg-emerald-500" label="Ongoing" count={lecturer.ongoingCount} />
          <SummaryDot color="bg-amber-400" label="Not Started" count={lecturer.notStartedCount} />
          <SummaryDot color="bg-slate-400" label="Completed" count={lecturer.completedCount} />
        </div>
      </div>

      {/* Subject section */}
      <div className="border-t border-gray-100">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <span className="text-xs font-medium text-gray-500">Filter:</span>
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                filter === opt.value
                  ? opt.color + ' ring-1 ring-offset-1 ring-gray-300'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              {opt.label}
              <span className="ml-1 text-gray-400">({filterCounts[opt.value]})</span>
            </button>
          ))}
        </div>
        <SubjectTable subjects={lecturer.subjects} filter={filter} />
      </div>
    </div>
  );
}

/* ─── main page ────────────────────────────────────────── */

export default function LecturerSubjects() {
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axiosInstance
      .get('/admin/dashboard/lecturer-subjects')
      .then((res) => {
        if (!cancelled) {
          setLecturers(res.data.lecturers || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load lecturer subjects.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let list = lecturers;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          (l.position || '').toLowerCase().includes(q) ||
          l.subjects.some(
            (s) =>
              (s.subjectName || '').toLowerCase().includes(q) ||
              (s.cohortLabel || '').toLowerCase().includes(q)
          )
      );
    }
    if (statusFilter === 'with-ongoing') {
      list = list.filter((l) => l.ongoingCount > 0);
    }
    return [...list].sort((a, b) => (b.ongoingCount ?? 0) - (a.ongoingCount ?? 0));
  }, [lecturers, search, statusFilter]);

  const totalOngoing = useMemo(
    () => lecturers.reduce((acc, l) => acc + l.ongoingCount, 0),
    [lecturers]
  );
  const totalSubjects = useMemo(
    () => lecturers.reduce((acc, l) => acc + l.totalSubjects, 0),
    [lecturers]
  );

  function handleDownloadExcel() {
    const rows = [];
    filtered.forEach((lecturer) => {
      const lecturerName = `${lecturer.title ? lecturer.title + ' ' : ''}${lecturer.name}`;
      lecturer.subjects.forEach((s) => {
        if (s.type === 'group') {
          s.groupSubjects.forEach((gs) => {
            rows.push({
              'Lecturer Name': lecturerName,
              'Position': lecturer.position || '',
              'Email': lecturer.officeEmail || '',
              'Subject Name': s.subjectName || '',
              'Subject Code': s.subjectCode || '',
              'Batch / Cohort': gs.cohortLabel || '',
              'Course': gs.courseShortName || gs.courseName || '',
              'Schedule Type': s.scheduleType || '',
              'Enrolled': gs.enrolledCount ?? '',
              'Start Date': gs.startDate || '',
              'End Date': gs.endDate || '',
              'Status': s.status || '',
              'Combined Class': `Yes — ${s.batchGroupName || 'Group'}`,
            });
          });
        } else {
          rows.push({
            'Lecturer Name': lecturerName,
            'Position': lecturer.position || '',
            'Email': lecturer.officeEmail || '',
            'Subject Name': s.subjectName || '',
            'Subject Code': s.subjectCode || '',
            'Batch / Cohort': s.cohortLabel || '',
            'Course': s.courseShortName || s.courseName || '',
            'Schedule Type': s.scheduleType || '',
            'Enrolled': s.enrolledCount ?? '',
            'Start Date': s.startDate || '',
            'End Date': s.endDate || '',
            'Status': s.status || '',
            'Combined Class': 'No',
          });
        }
      });
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto column widths
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String(r[key] ?? '').length)) + 2,
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lecturer Subjects');

    const filterLabel = statusFilter === 'with-ongoing' ? '_ongoing' : '';
    const searchLabel = search.trim() ? `_${search.trim().replace(/\s+/g, '-').toLowerCase()}` : '';
    XLSX.writeFile(wb, `lecturer-subjects${filterLabel}${searchLabel}.xlsx`);
  }

  return (
    <DashboardLayout title="Lecturer Subjects">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Lecturer Subjects</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track subject assignments for each lecturer across all batches and courses.
          </p>
        </div>

        {/* Summary stats */}
        {!loading && !error && (
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Lecturers</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{lecturers.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total Assignments</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{totalSubjects}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-600">Ongoing</p>
              <p className="mt-1 text-2xl font-bold text-emerald-700">{totalOngoing}</p>
            </div>
          </div>
        )}

        {/* Toolbar */}
        {!loading && !error && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search lecturer, subject, or batch…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm text-gray-700 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="all">All Lecturers</option>
              <option value="with-ongoing">With Ongoing Subjects</option>
            </select>
            <span className="text-sm text-gray-400">
              {filtered.length} lecturer{filtered.length !== 1 ? 's' : ''} shown
            </span>
            <button
              onClick={handleDownloadExcel}
              disabled={filtered.length === 0}
              className="ml-auto flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 shadow-sm hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
              </svg>
              Download Excel
            </button>
          </div>
        )}

        {/* States */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <svg className="h-8 w-8 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="ml-3 text-sm text-gray-500">Loading lecturer data…</span>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm">
            <svg className="mx-auto mb-3 h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5.477-3.694M9 20H4v-2a4 4 0 015.477-3.694M15 7a4 4 0 11-8 0 4 4 0 018 0zm6 3a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm text-gray-400">No lecturers found.</p>
          </div>
        )}

        {/* Lecturer cards */}
        {!loading && !error && (
          <div className="flex flex-col gap-4">
            {filtered.map((lecturer) => (
              <LecturerCard key={lecturer.staffId} lecturer={lecturer} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}